import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, shareReplay, catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Configuration } from '../models/configuration';
import { RuntimeSettings } from '../models/runtime-settings';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  readonly API_URL = `${environment.apiUrl}/configuration`;

  // Cache variables
  private cachedRuntimeSettings: RuntimeSettings | null = null;
  private runtimeSettings$: Observable<RuntimeSettings> | null = null;

  userLocationSource$ = new BehaviorSubject<String | undefined>('');
  constructor(private http: HttpClient) {}

  getConfiguration(): Observable<Configuration> {
    return this.http.get<Configuration>(`${this.API_URL}`);
  }

  getRuntimeSettings(): Observable<RuntimeSettings> {
    // Check if settings are already cached
    if (this.cachedRuntimeSettings) {
      return of(this.cachedRuntimeSettings);
    }

    // Return the ongoing request if it exists
    if (this.runtimeSettings$) {
      return this.runtimeSettings$;
    }

    // Make HTTP request and cache the result
    this.runtimeSettings$ = this.http.get<RuntimeSettings>(`${this.API_URL}/runtime-settings`).pipe(
      map((res: RuntimeSettings) => this.buildRuntimeSettingsWithContractNames(res)),
      tap((augmented: RuntimeSettings) => {
        this.cachedRuntimeSettings = augmented;
      }),
      shareReplay(1),
      catchError((error) => {
        // Reset the observable on error
        throw error; // Rethrow the error to be handled by subscribers
      }),
    );

    return this.runtimeSettings$;
  }

  getAppType(): Observable<string> {
    return this.http.get(`${this.API_URL}/app-type`, { responseType: 'text' });
  }

  updateRuntimeSettings(runtimeSettings: RuntimeSettings): Observable<RuntimeSettings> {
    this.runtimeSettings$ = this.http.put<RuntimeSettings>(`${this.API_URL}/runtime-settings`, runtimeSettings).pipe(
      map((res: RuntimeSettings) => this.buildRuntimeSettingsWithContractNames(res)),
      tap((augmented: RuntimeSettings) => {
        this.cachedRuntimeSettings = augmented;
      }),
      shareReplay(1),
      catchError((error) => {
        // Reset the observable on error
        this.runtimeSettings$ = null;
        throw error; // Rethrow the error to be handled by subscribers
      }),
    );
    return this.runtimeSettings$;
  }

  private buildRuntimeSettingsWithContractNames(settings: RuntimeSettings): RuntimeSettings {
    return {
      ...settings,
      contractNames: [
        ...(settings.entryLevelContractName || []),
        ...(settings.permanentContractName || []),
        ...(settings.subContractorContractName || []),
      ],
    };
  }

   getCachedRuntimeSettings():  RuntimeSettings |  null  {
    return this.cachedRuntimeSettings;
 }

}
